import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import ListaNarzedzi from './ListaNarzedzi'

/**
 * Przycisk „Narzędzia” w górnym pasku i panel ze wszystkimi narzędziami w czterech kolumnach
 * (grupy z `config/nawigacja`, te same co w szufladzie na telefonie).
 *
 * ═══ GDZIE STOI PANEL ═══
 * Przy prawej krawędzi CAŁEGO paska, nie przycisku: panel ma ok. 800 px, a przycisk przy 1024 px
 * stoi bliżej środka, więc panel wyrównany do niego wyjeżdżał w lewo poza ekran. Dlatego opakowanie
 * NIE ma `relative`: punktem odniesienia jest `<nav>` paska (`sticky`). Kto doda tu `relative`,
 * przeniesie panel pod przycisk.
 *
 * Zamyka się: Escape (fokus wraca na przycisk), klik poza menu, wybór pozycji, zmiana strony
 * i wyjście z menu klawiszem Tab. Po otwarciu fokus trafia na pierwszą pozycję.
 */
const MenuNarzedzi = ({ id = 'menu-narzedzi' }) => {
  const { t } = useTranslation('wspolne')
  const [otwarte, setOtwarte] = useState(false)
  const opakowanie = useRef(null)
  const przycisk = useRef(null)
  const panel = useRef(null)
  const { pathname } = useLocation()

  const zamknij = useCallback(() => setOtwarte(false), [])

  useEffect(() => { setOtwarte(false) }, [pathname])

  useEffect(() => {
    if (!otwarte) return undefined
    panel.current?.querySelector('a[href]')?.focus()

    const pozaMenu = (e) => {
      if (opakowanie.current && !opakowanie.current.contains(e.target)) setOtwarte(false)
    }
    const klawisz = (e) => {
      if (e.key !== 'Escape') return
      setOtwarte(false)
      przycisk.current?.focus()
    }
    document.addEventListener('mousedown', pozaMenu)
    document.addEventListener('keydown', klawisz)
    return () => {
      document.removeEventListener('mousedown', pozaMenu)
      document.removeEventListener('keydown', klawisz)
    }
  }, [otwarte])

  // Tab poza ostatnią pozycję (albo Shift+Tab przed przycisk) zamyka menu: otwarty panel nad
  // stroną, w którym nie ma już fokusu, tylko zasłania treść. Bez `relatedTarget` (klik w puste
  // miejsce panelu, przejście do innego okna) menu zostaje; klik poza nim łapie `pozaMenu`.
  const opuszczenie = (e) => {
    const cel = e.relatedTarget
    if (cel && opakowanie.current && !opakowanie.current.contains(cel)) setOtwarte(false)
  }

  return (
    <div ref={opakowanie} onBlur={opuszczenie}>
      <button
        ref={przycisk}
        type='button'
        onClick={() => setOtwarte((o) => !o)}
        aria-haspopup='true'
        aria-expanded={otwarte}
        aria-controls={id}
        className={`flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-lg text-sm font-semibold transition-colors
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                    ${otwarte
                      ? 'text-blue-700 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'}`}
      >
        <span className='whitespace-nowrap'>{t('narzedzia.przycisk')}</span>
        <ChevronDownIcon
          aria-hidden='true'
          className={`w-4 h-4 flex-shrink-0 transition-transform ${otwarte ? 'rotate-180' : ''}`}
        />
      </button>

      {otwarte && (
        <div
          ref={panel}
          id={id}
          className='absolute top-full right-4 md:right-8 mt-2 z-50 max-w-[calc(100vw-2rem)]
                     rounded-2xl border border-gray-200 bg-white shadow-xl p-4'
        >
          <ListaNarzedzi kolumny idPrefix='menu' onWybor={zamknij} />
        </div>
      )}
    </div>
  )
}

export default MenuNarzedzi
